import BoxWithShadow from "../../common/components/BoxWithShadow";
import Table from "../../common/components/Table";
import BlackOverlay from "./BlackOverlay";
import BasicButton from "../../common/components/BasicButton";
import { twMerge } from "tailwind-merge";
import { FETCH_SIZE } from "../../../utilities/constants";
import { formatDate } from "../../../utilities/format";
import { getTotalPages } from "../../../utilities/pagination";
import { SortDirection } from "global";
import { useForm } from "../../../hooks/useForm";
import ListPageTopSection from "../../common/components/ListPageTopSection";
import SearchBar from "../../common/components/SearchBar";
import SelectBox from "../../common/components/SelectBox";
import {
  NetworkUserSearchType,
  NetworkUserSortKey,
  useGetReferralNetworkUsersAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import { NetworkOwner } from "../containers/NetworkListContainer";

interface NetworkListProps {
  ownerId: string | null;
  onOwnerSelect: (ownerId: NetworkOwner) => void;
}

export default function NetworkList({
  ownerId,
  onOwnerSelect,
}: NetworkListProps) {
  const [filters, onChange, onBatch] = useForm({
    query: "",
    page: 1,
    networkUserSearchType: NetworkUserSearchType.CaregiverName,
    sortKey: NetworkUserSortKey.CreatedAt,
    sortDirection: "DESC" as SortDirection,
  });

  const onSort = (key?: NetworkUserSortKey, direction?: SortDirection) => {
    const updatedFilters = {
      ...filters,
      sortKey: key,
      sortDirection: direction,
    };

    onBatch(updatedFilters);
    refetch(updatedFilters);
  };

  const onSearch = () => {
    console.table(filters);
    onChange("page", 1);
    refetch({
      ...filters,
      page: 1,
      limit: FETCH_SIZE,
    });
  };

  const { data, loading, refetch } = useGetReferralNetworkUsersAsAdminQuery({
    variables: {
      page: 1,
      limit: FETCH_SIZE,
    },
  });

  const networks = data?.getReferralNetworkUsersAsAdmin?.edges || [];
  const totalPages = getTotalPages(
    data?.getReferralNetworkUsersAsAdmin?.pageInfo.totalCount
  );

  return (
    <BoxWithShadow
      className={twMerge(
        "relative w-full gap-3 overflow-auto transition-all duration-300",
        !!ownerId && "w-1/6"
      )}
    >
      <BlackOverlay
        isShown={!!ownerId}
        message={"다른 네트워크를\n조회하고 싶으신가요?"}
        icon={
          <BasicButton
            text="네트워크 선택"
            variant="error"
            className="w-full"
            onClick={() => onOwnerSelect({ id: null, type: null })}
          />
        }
      />
      <p className="font-bold">네트워크 목록</p>
      <ListPageTopSection>
        <SelectBox
          value={filters.networkUserSearchType}
          onSelect={(value) => onChange("networkUserSearchType", value)}
          options={[
            {
              label: "보호사 이름",
              value: NetworkUserSearchType.CaregiverName,
            },
            {
              label: "보호자 이름",
              value: NetworkUserSearchType.GuardianName,
            },
          ]}
        />
        <SearchBar
          value={filters.query}
          onSearch={onSearch}
          onChange={(value) => onChange("query", value)}
        />
      </ListPageTopSection>
      <Table.Wrapper>
        <Table.Header
          sort={{
            key: filters.sortKey,
            direction: filters.sortDirection as SortDirection,
            onSort,
          }}
          tabs={[
            { label: "상위 사용자" },
            { label: "사용자 닉네임" },
            { label: "사용자 구분" },
            {
              label: "맴버 (전체)",
              sortKey: NetworkUserSortKey.TotalNetworkUserCount,
            },
            {
              label: "네트워크 생성일자",
              sortKey: NetworkUserSortKey.CreatedAt,
            },
            {
              label: "리워드 (전체)",
            },
            { label: "최근 접속일" },
          ]}
        />
        <Table.Body isLoading={loading} isEmpty={!networks?.length}>
          {networks?.map((network) => {
            const isCaregiver = !!network?.caregiver;
            const owner = network?.caregiver || network?.guardian;

            return (
              <Table.Row
                key={owner?.id}
                onClick={() =>
                  onOwnerSelect({
                    id: owner?.id || null,
                    type: isCaregiver ? "caregivers" : "guardians",
                  })
                }
              >
                <Table.Column>{owner?.name}</Table.Column>
                <Table.Column>{owner?.username}</Table.Column>
                <Table.Column>
                  {!!network?.caregiver ? "보호사" : "보호자"}
                </Table.Column>
                <Table.Column>
                  {network?.totalNetworkUserCount || 0}명
                </Table.Column>
                <Table.Column>
                  {formatDate(network?.createdAt, "YY년 MM월 DD일")}
                </Table.Column>
                <Table.Column>
                  {network?.totalNetworkRewardPoint?.toLocaleString()}원
                </Table.Column>
                <Table.Column>
                  {formatDate(owner?.lastSignInAt, "YY년 MM월 DD일")}
                </Table.Column>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Wrapper>
      <Table.Pagination
        page={filters.page}
        total={totalPages}
        onPageChange={(newPage) => onChange("page", newPage)}
      />
    </BoxWithShadow>
  );
}
